import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import {
  parseDeliveryFeeCents,
  useSaveStoreFulfillmentSettings,
  useStoreFulfillmentSettings,
  useTranslation,
  type StoreFulfillmentSettings,
  type TranslationKey,
} from '@dukanti/shared';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useNavBlocker } from '@/hooks/use-nav-blocker';

const draftSchema = z
  .object({
    fee: z.string(),
    pickupEnabled: z.boolean(),
    pickupName: z.string().trim(),
    pickupDetails: z.string().trim(),
  })
  .superRefine((draft, context) => {
    try {
      parseDeliveryFeeCents(draft.fee);
    } catch {
      context.addIssue({
        code: 'custom',
        path: ['fee'],
        message: 'admin.storeSettings.fulfillment.feeInvalid',
      });
    }
    if (draft.pickupEnabled && (draft.pickupName.length < 1 || draft.pickupName.length > 160)) {
      context.addIssue({
        code: 'custom',
        path: ['pickupName'],
        message: 'admin.storeSettings.fulfillment.pickupNameInvalid',
      });
    }
    if (
      draft.pickupEnabled &&
      (draft.pickupDetails.length < 1 || draft.pickupDetails.length > 1000)
    ) {
      context.addIssue({
        code: 'custom',
        path: ['pickupDetails'],
        message: 'admin.storeSettings.fulfillment.pickupDetailsInvalid',
      });
    }
  });
type FulfillmentDraft = z.infer<typeof draftSchema>;
function defaults(settings: StoreFulfillmentSettings): FulfillmentDraft {
  return {
    fee: settings.deliveryFeeCents === null ? '' : (settings.deliveryFeeCents / 100).toFixed(2),
    pickupEnabled: settings.pickupPlaces.length > 0,
    pickupName: settings.pickupPlaces[0]?.name ?? '',
    pickupDetails: settings.pickupPlaces[0]?.details ?? '',
  };
}

export function FulfillmentSettingsForm({
  storeId,
}: {
  readonly storeId: string;
}): React.JSX.Element {
  const { t, dir } = useTranslation();
  const settings = useStoreFulfillmentSettings(supabase, storeId);
  const save = useSaveStoreFulfillmentSettings(supabase);
  const [baseline, setBaseline] = useState<StoreFulfillmentSettings | null>(null);
  const [error, setError] = useState<TranslationKey | null>(null);
  const [confirmation, setConfirmation] = useState<FulfillmentDraft | null>(null);
  const saveTriggerRef = useRef<HTMLButtonElement>(null);
  const form = useForm<FulfillmentDraft>({
    resolver: zodResolver(draftSchema),
    defaultValues: { fee: '', pickupEnabled: false, pickupName: '', pickupDetails: '' },
  });
  const pickupEnabled = form.watch('pickupEnabled');
  const deliveryFeeDraft = form.watch('fee');
  useNavBlocker(form.formState.isDirty);
  useEffect(() => {
    if (settings.data && !baseline) {
      setBaseline(settings.data);
      form.reset(defaults(settings.data));
    }
  }, [settings.data, baseline, form]);

  async function persist(draft: FulfillmentDraft): Promise<void> {
    if (!baseline || save.isPending) return;
    setError(null);
    try {
      const result = await save.mutateAsync({
        storeId,
        expectedRevision: baseline.revision,
        currency: 'ILS',
        deliveryFeeCents: parseDeliveryFeeCents(draft.fee),
        pickupPlaces: draft.pickupEnabled
          ? [
              {
                id: baseline.pickupPlaces[0]?.id ?? null,
                name: draft.pickupName,
                details: draft.pickupDetails,
              },
            ]
          : [],
      });
      if (!result.success) {
        setError(
          result.error_code === 'FULFILLMENT_CONFIG_CONFLICT'
            ? 'admin.storeSettings.fulfillment.conflict'
            : result.error_code === 'PICKUP_PLACE_LIMIT'
              ? 'admin.storeSettings.fulfillment.pickupLimit'
              : 'admin.storeSettings.fulfillment.saveFailed',
        );
        return;
      }
      setBaseline(result.value);
      form.reset(defaults(result.value));
      toast.success(t('admin.storeSettings.fulfillment.saved'));
    } catch {
      setError('admin.storeSettings.fulfillment.saveFailed');
    } finally {
      setConfirmation(null);
    }
  }
  async function reload(): Promise<void> {
    const result = await settings.refetch();
    if (result.data && !result.isError) {
      setBaseline(result.data);
      form.reset(defaults(result.data));
      setError(null);
    }
  }
  function submit(draft: FulfillmentDraft): void {
    if (parseDeliveryFeeCents(draft.fee) === null && !draft.pickupEnabled) setConfirmation(draft);
    else void persist(draft);
  }
  if (!baseline)
    return (
      <section
        className="border-border bg-card shadow-admin-card mt-6 rounded-xl border p-6"
        aria-live="polite"
      >
        <h2 className="font-display text-xl">{t('admin.storeSettings.fulfillment.title')}</h2>
        <p role={settings.isError ? 'alert' : 'status'}>
          {t(settings.isError ? 'fulfillment.loadFailed' : 'common.loading')}
        </p>
        {settings.isError && (
          <Button type="button" onClick={() => void reload()}>
            {t('common.retry')}
          </Button>
        )}
      </section>
    );
  return (
    <form
      dir={dir}
      onSubmit={form.handleSubmit(submit)}
      data-testid="fulfillment-settings-form"
      className="border-border bg-card shadow-admin-card mt-6 space-y-5 rounded-xl border p-6 sm:p-8"
    >
      <h2 className="font-display text-foreground text-xl font-bold">
        {t('admin.storeSettings.fulfillment.title')}
      </h2>
      <div>
        <label htmlFor="fulfillment-fixed-fee" className="text-sm font-medium">
          {t('fulfillment.deliveryFee')}
        </label>
        <Input
          id="fulfillment-fixed-fee"
          inputMode="decimal"
          dir="ltr"
          disabled={save.isPending}
          {...form.register('fee')}
          aria-invalid={!!form.formState.errors.fee}
          aria-describedby="fulfillment-fee-help fulfillment-fee-error"
        />
        <p id="fulfillment-fee-help" className="text-muted-foreground mt-2 text-sm">
          {t('admin.storeSettings.fulfillment.fixedFeeHelp')}
        </p>
        {form.formState.errors.fee && (
          <p
            id="fulfillment-fee-error"
            role="alert"
            className="text-sm text-red-700 dark:text-red-300"
          >
            {t('admin.storeSettings.fulfillment.feeInvalid')}
          </p>
        )}
      </div>
      <div className="border-border space-y-4 rounded-xl border p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="font-semibold">{t('fulfillment.pickupPlace')}</h3>
          <Button
            type="button"
            variant="outline"
            disabled={save.isPending}
            onClick={() => form.setValue('pickupEnabled', !pickupEnabled, { shouldDirty: true })}
          >
            {t(
              pickupEnabled
                ? 'admin.storeSettings.fulfillment.removePickup'
                : 'admin.storeSettings.fulfillment.addPickup',
            )}
          </Button>
        </div>
        <p className="text-muted-foreground text-sm">
          {t('admin.storeSettings.fulfillment.pickupLimit')}
        </p>
        {pickupEnabled ? (
          <>
            <div>
              <label htmlFor="fulfillment-pickup-name" className="text-sm">
                {t('admin.storeSettings.fulfillment.pickupName')}
              </label>
              <Input
                id="fulfillment-pickup-name"
                {...form.register('pickupName')}
                maxLength={160}
                disabled={save.isPending}
                aria-invalid={!!form.formState.errors.pickupName}
                aria-describedby={
                  form.formState.errors.pickupName ? 'pickup-name-error' : undefined
                }
              />
              {form.formState.errors.pickupName && (
                <p
                  id="pickup-name-error"
                  role="alert"
                  className="text-sm text-red-700 dark:text-red-300"
                >
                  {t('admin.storeSettings.fulfillment.pickupNameInvalid')}
                </p>
              )}
            </div>
            <div>
              <label htmlFor="fulfillment-pickup-details" className="text-sm">
                {t('admin.storeSettings.fulfillment.pickupDetails')}
              </label>
              <Textarea
                id="fulfillment-pickup-details"
                {...form.register('pickupDetails')}
                maxLength={1000}
                disabled={save.isPending}
                aria-invalid={!!form.formState.errors.pickupDetails}
                aria-describedby={
                  form.formState.errors.pickupDetails ? 'pickup-details-error' : undefined
                }
              />
              {form.formState.errors.pickupDetails && (
                <p
                  id="pickup-details-error"
                  role="alert"
                  className="text-sm text-red-700 dark:text-red-300"
                >
                  {t('admin.storeSettings.fulfillment.pickupDetailsInvalid')}
                </p>
              )}
            </div>
          </>
        ) : (
          <p className="text-muted-foreground text-sm">
            {t('admin.storeSettings.fulfillment.removeDescription')}
          </p>
        )}
      </div>
      {deliveryFeeDraft.trim() === '' && !pickupEnabled ? (
        <p
          role="status"
          className="border-honey/40 bg-honey/10 text-foreground rounded-xl border p-4 text-sm"
        >
          {t('admin.storeSettings.fulfillment.noMethodsWarning')}
        </p>
      ) : null}
      {error && (
        <div role="alert" className="space-y-2 text-sm text-red-700 dark:text-red-300">
          <p>{t(error)}</p>
          {error === 'admin.storeSettings.fulfillment.conflict' && (
            <Button
              type="button"
              variant="outline"
              disabled={settings.isFetching}
              onClick={() => void reload()}
            >
              {t('admin.storeSettings.fulfillment.reload')}
            </Button>
          )}
        </div>
      )}
      <div className="flex flex-wrap justify-end gap-2">
        <Button
          type="button"
          variant="ghost"
          disabled={save.isPending || !form.formState.isDirty}
          onClick={() => {
            form.reset(defaults(baseline));
            setError(null);
          }}
        >
          {t('common.cancel')}
        </Button>
        <Button
          type="submit"
          ref={saveTriggerRef}
          disabled={save.isPending || !form.formState.isDirty}
          aria-busy={save.isPending}
        >
          {t(save.isPending ? 'common.loading' : 'common.save')}
        </Button>
      </div>
      <AlertDialog
        open={confirmation !== null}
        onOpenChange={(open) => {
          if (!open && !save.isPending) setConfirmation(null);
        }}
      >
        <AlertDialogContent
          dir={dir}
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            if (saveTriggerRef.current && !saveTriggerRef.current.disabled)
              saveTriggerRef.current.focus();
            else document.getElementById('fulfillment-fixed-fee')?.focus();
          }}
        >
          <AlertDialogHeader>
            <AlertDialogTitle>{t('admin.storeSettings.fulfillment.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('admin.storeSettings.fulfillment.noMethodsWarning')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel asChild>
              <Button type="button" variant="ghost" disabled={save.isPending}>
                {t('common.cancel')}
              </Button>
            </AlertDialogCancel>
            <Button
              type="button"
              disabled={save.isPending}
              onClick={() => {
                if (confirmation) void persist(confirmation);
              }}
            >
              {t('common.save')}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </form>
  );
}
